import React, { useCallback, useState } from 'react';
import { HardDrive, Monitor, Terminal, Trash2 } from 'lucide-react';
import { useSimulation } from '../context/SimulationContext';
import { inferDesktopApp, windowTitle } from '../utils/inferDesktopApp';
import { WindowFrame } from './WindowFrame';
import { CommandPrompt } from './apps/CommandPrompt';
import { NetworkSettings } from './apps/NetworkSettings';
import { Taskbar } from './Taskbar';

function titleIcon(kind: Parameters<typeof windowTitle>[0]): React.ReactNode {
  switch (kind) {
    case 'network-settings':
      return <Monitor className="text-blue-300" aria-hidden />;
    case 'command-prompt':
      return <Terminal className="text-slate-200" aria-hidden />;
    case 'this-pc':
      return <HardDrive className="text-sky-300" aria-hidden />;
    case 'recycle-bin':
      return <Trash2 className="text-slate-300" aria-hidden />;
    default:
      return null;
  }
}

export function Desktop(): React.ReactElement {
  const { scenario, state, openWindow } = useSimulation();
  const [minimizedMap, setMinimizedMap] = useState<Record<string, boolean>>({});

  const setMin = useCallback((id: string, v: boolean) => {
    setMinimizedMap((m) => ({ ...m, [id]: v }));
  }, []);

  return (
    <div
      data-tutorial="desktop"
      className="flex min-h-[720px] h-screen min-w-[320px] w-full flex-col bg-gradient-to-br from-[#0a0a2e] via-[#1a1a4e] to-[#0d0d3d] text-white overflow-hidden select-none"
    >
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0 p-6 grid grid-flow-col auto-cols-max grid-rows-[repeat(auto-fill,96px)] gap-x-6 gap-y-8 content-start">
          {scenario.desktopIcons.map((ico) => (
            <button
              key={ico.id}
              type="button"
              data-tutorial={
                ico.opensApp === 'command-prompt'
                  ? 'cmd-desktop-icon'
                  : ico.opensApp === 'network-settings'
                    ? 'network-settings-icon'
                    : undefined
              }
              onDoubleClick={() => openWindow(ico.opensApp)}
              className="group flex w-[88px] flex-col items-center gap-1 rounded-lg p-2 text-center outline-none focus-visible:ring-2 focus-visible:ring-white/40 hover:bg-white/5 transition-colors cursor-pointer"
            >
              <span
                className="text-4xl drop-shadow-lg group-hover:scale-105 transition-transform"
                aria-hidden
              >
                {ico.emoji}
              </span>
              <span className="text-xs leading-tight text-white/90 line-clamp-2 drop-shadow-md">
                {ico.label}
              </span>
            </button>
          ))}
        </div>

        {state.openWindows.map((wid) => {
          const kind = inferDesktopApp(wid);
          if (!kind) return null;
          const title = windowTitle(kind);
          const positions: Record<string, { x: number; y: number }> = {
            'network-settings': { x: 120, y: 80 },
            'command-prompt': { x: 180, y: 120 },
            'this-pc': { x: 200, y: 100 },
            'recycle-bin': { x: 220, y: 110 },
          };
          const basePos = positions[kind] ?? { x: 140, y: 100 };
          const offset =
            kind === 'command-prompt'
              ? (wid.charCodeAt(wid.length - 1) % 5) * 12
              : 0;
          const position = {
            x: basePos.x + offset,
            y: basePos.y + offset,
          };

          let body: React.ReactNode = null;
          let defaultSize = { w: 640, h: 420 };

          switch (kind) {
            case 'network-settings':
              body = <NetworkSettings windowId={wid} />;
              defaultSize = { w: 640, h: 480 };
              break;
            case 'command-prompt':
              body = <CommandPrompt windowId={wid} />;
              defaultSize = { w: 720, h: 440 };
              break;
            case 'this-pc':
              body = (
                <StubApp
                  headline="This PC"
                  body="Folders and devices on this PC are not available in the assessment sandbox."
                />
              );
              break;
            case 'recycle-bin':
              body = (
                <StubApp
                  headline="Recycle Bin"
                  body="The Recycle Bin is empty."
                />
              );
              break;
            default:
              body = null;
          }

          return (
            <WindowFrame
              key={wid}
              windowId={wid}
              title={title}
              titleIcon={titleIcon(kind)}
              defaultPosition={position}
              defaultSize={defaultSize}
              minimized={!!minimizedMap[wid]}
              onMinimize={() => setMin(wid, true)}
              dataTutorialWindow={
                kind === 'command-prompt' ? 'command-prompt-window' : undefined
              }
            >
              {body}
            </WindowFrame>
          );
        })}
      </div>

      <Taskbar
        minimizedMap={minimizedMap}
        onRestore={(id) => setMin(id, false)}
      />
    </div>
  );
}

function StubApp({
  headline,
  body,
}: {
  headline: string;
  body: string;
}): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col gap-2 p-4 bg-black/15 text-white/90">
      <h2 className="text-lg font-semibold">{headline}</h2>
      <p className="text-sm text-white/70 leading-relaxed">{body}</p>
    </div>
  );
}
